import { Alert, AlertTitle, Button, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
    TRIAL_TEST_MINUTES,
    formatPriceMwk,
    getPlanById,
    getTrialRemainingLabel,
    isShortTestTrial,
    isTrialExpired,
    subscriptionBlockedMessage,
} from "../../config/subscriptionPlans";
import { useSubscriptionClock } from "../../hooks/useSubscriptionClock";

/**
 * Trial / plan reminder on the office dashboard (before payment is required).
 */
export default function SubscriptionStatusBanner({ user }) {
    useSubscriptionClock(user);

    if (!user || user.subscriptionStatus === "lifetime") return null;

    const plan = getPlanById(user.subscriptionPlan);
    const count = user.schoolStudentCount ?? 0;
    const limit = user.studentLimit;
    const atLimit = limit != null && count >= limit;
    const nearLimit = limit != null && count >= limit - 5 && count < limit;

    if (user.subscriptionStatus === "trialing" || user.subscriptionStatus === "expired") {
        if (isTrialExpired(user) || user.subscriptionStatus === "expired") {
            return (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    <AlertTitle>Free trial ended — {plan.name} plan</AlertTitle>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {subscriptionBlockedMessage({
                            ...user,
                            subscriptionReason: user.subscriptionReason || "trial_expired",
                            subscriptionStatus: "expired",
                        })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Use the payment dialog to activate your subscription and regain full access.
                    </Typography>
                </Alert>
            );
        }

        const dayLabel = getTrialRemainingLabel(user);

        return (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <AlertTitle>Free trial — {plan.name} plan</AlertTitle>
                <Typography variant="body2">
                    {dayLabel ? (
                        <>
                            You have <strong>{dayLabel}</strong>. After that, pay {formatPriceMwk(plan.priceMwk)} (
                            {plan.billingLabel}) to keep using the system. Every school gets one trial only.
                        </>
                    ) : (
                        <>
                            Your free trial is active. After it ends, pay {formatPriceMwk(plan.priceMwk)} (
                            {plan.billingLabel}) to keep using the system. Every school gets one trial only.
                        </>
                    )}
                    {TRIAL_TEST_MINUTES > 0 && isShortTestTrial(user) ? (
                        <>
                            {" "}
                            <strong>(Test mode: {TRIAL_TEST_MINUTES}-minute trial for new schools only.)</strong>
                        </>
                    ) : null}
                </Typography>
                {limit != null && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Student register: <strong>{count}</strong> of <strong>{limit}</strong> allowed on this plan.
                    </Typography>
                )}
            </Alert>
        );
    }

    if (user.subscriptionStatus === "active") {
        const days = user.subscriptionDaysRemaining;
        return (
            <Alert severity="success" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                <AlertTitle>Subscription active — {plan.name}</AlertTitle>
                <Typography variant="body2">
                    {days != null ? (
                        <>
                            Renews in <strong>{days}</strong> day{days === 1 ? "" : "s"}.
                        </>
                    ) : (
                        <>Your school subscription is active.</>
                    )}
                    {limit != null && (
                        <>
                            {" "}
                            Students: <strong>{count}</strong> / <strong>{limit}</strong>.
                        </>
                    )}
                </Typography>
            </Alert>
        );
    }

    if (atLimit) {
        return (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                <AlertTitle>Student limit reached</AlertTitle>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Your {plan.name} plan allows up to {limit} students. You cannot add more until you upgrade your plan.
                    Contact support to move to Standard, Professional, or Enterprise.
                </Typography>
                <Button size="small" component={RouterLink} to="/platform#pricing" target="_blank" rel="noopener">
                    View plans
                </Button>
            </Alert>
        );
    }

    if (nearLimit) {
        return (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="body2">
                    You are near your plan limit ({count} of {limit} students). Consider upgrading before enrolment
                    closes.
                </Typography>
            </Alert>
        );
    }

    return null;
}
